import { useCallback, useEffect, useRef, useState } from "react";
import { AudioAnalyzer } from "./audioAnalyzer";
import { TrackAnalysisService } from "./trackAnalysisService";
import { DEFAULT_PRESETS, HapticPreset, TrackAnalysis } from "./types";

export const useHapticEngine = (audioContext: AudioContext | null) => {
  const analyzerRef = useRef<AudioAnalyzer | null>(null);
  const currentAnalysisRef = useRef<TrackAnalysis | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [settings, setSettings] = useState({
    enabled: false,
    currentPreset: "pop" as string, // Set default preset
  });
  const [livePresetConfig, setLivePresetConfig] = useState<HapticPreset | null>(null);

  // Initialize or update livePresetConfig when currentPreset changes
  useEffect(() => {
    const currentPresetDetails = DEFAULT_PRESETS[settings.currentPreset];
    if (currentPresetDetails) {
      // Create a deep copy for live editing, especially for nested objects
      setLivePresetConfig(JSON.parse(JSON.stringify(currentPresetDetails)));
    } else {
      setLivePresetConfig(null);
    }
  }, [settings.currentPreset]);

  useEffect(() => {
    if (audioContext) {
      console.log("=== hapticsengine: Initializing haptics analyzer with audio context:", {
        sampleRate: audioContext.sampleRate,
        state: audioContext.state,
      });

      // Store context reference (if still needed for other purposes)
      audioContextRef.current = audioContext;
      // Instantiate AudioAnalyzer without context
      analyzerRef.current = new AudioAnalyzer();

      // Cleanup function
      return () => {
        if (audioContextRef.current && audioContextRef.current.state !== "closed") {
          audioContextRef.current.close();
          console.log("=== hapticsengine: Closing audio context for haptics analyzer");
        }
      };
    }
  }, [audioContext]);

  const updateSettings = useCallback(
    (newSettings: Partial<typeof settings>) => {
      console.log("=== hapticsengine: Updating haptics settings from:", settings, "to:", newSettings); // Add logging
      setSettings((prev) => {
        const updated = { ...prev, ...newSettings };
        return updated;
      });
    },
    [settings]
  ); // Add settings dependency

  const updateLivePresetConfig = useCallback((newConfig: Partial<HapticPreset>) => {
    setLivePresetConfig(prev => {
      if (!prev) return null;
      
      const updatedConfig = { ...prev, ...newConfig };

      // Ensure nested objects like targetFrequencyBand are merged correctly if partially updated
      if (newConfig.targetFrequencyBand) {
        updatedConfig.targetFrequencyBand = {
          ...prev.targetFrequencyBand, // Default to existing values if any
          ...(prev.targetFrequencyBand || {}), // Ensure prev.targetFrequencyBand is not null
          ...newConfig.targetFrequencyBand,
        };
      }
      return updatedConfig;
    });
  }, []);

  const analyzeTrack = useCallback(
    async (audioBuffer: AudioBuffer, trackId: string, presetName: string) => {
      console.log("=== hapticsengine: Starting track analysis:", { trackId, presetName });

      try {
        // Ensure analyzerRef.current is initialized.
        // It should be initialized by the useEffect hook, but as a fallback or if called early:
        if (!analyzerRef.current) {
          analyzerRef.current = new AudioAnalyzer();
          console.log("=== hapticsengine: Created new analyzer instance in analyzeTrack (fallback).");
        }

        // Prioritize livePresetConfig if the analysis is for the currently active preset
        const presetConfigForAnalysis = 
          (presetName === settings.currentPreset && livePresetConfig) 
            ? livePresetConfig 
            : DEFAULT_PRESETS[presetName];

        if (!presetConfigForAnalysis) {
          console.error("=== hapticsengine: Invalid preset configuration for:", presetName);
          return null;
        }
        
        // No need to check for existing analysis here if we want to force re-analysis with new live config.
        // If we want to use cached analysis, that logic would need to be adapted based on whether config changed.
        // For now, let's assume analyzeTrack means "do the analysis with current config".

        console.log("=== hapticsengine: Analyzing with preset:", presetConfigForAnalysis.name, presetConfigForAnalysis);
        const analysis = await analyzerRef.current.analyzeTrack(audioBuffer, presetConfigForAnalysis);
        analysis.trackId = trackId;

        console.log("=== hapticsengine: Analysis complete:", {
          trackId,
          points: analysis.feedbackPoints.length,
        });

        // Save analysis. If livePresetConfig was used, this analysis is specific to those settings.
        // The TrackAnalysisService might need adaptation if we want to cache based on config details, not just trackId.
        // For now, it will overwrite if trackId is the same.
        await TrackAnalysisService.saveAnalysis(trackId, analysis);
        currentAnalysisRef.current = analysis;
        // Ensure settings.currentPreset reflects the presetName that was analyzed, if it was different.
        // However, analyzeTrack is usually called with settings.currentPreset.
        if (presetName !== settings.currentPreset) {
            updateSettings({ currentPreset: presetName });
        }


        return analysis;
      } catch (error) {
        console.error("=== hapticsengine: Analysis failed:", error);
        return null;
      }
    },
    [settings.currentPreset, livePresetConfig, updateSettings] // Added livePresetConfig
  );

  const provideFeedback = useCallback(
    (currentTime: number) => {
      if (!currentAnalysisRef.current || !settings.enabled) {
        return;
      }

      // Use livePresetConfig for feedback if it's the active preset
      const presetForFeedback = 
        (livePresetConfig && livePresetConfig.name === settings.currentPreset)
          ? livePresetConfig
          : DEFAULT_PRESETS[settings.currentPreset || "pop"];
      
      if (!presetForFeedback) {
        // console.warn("=== hapticsengine: No preset found for feedback for key:", settings.currentPreset);
        return;
      }

      const latencyComp = presetForFeedback.latencyCompensation ?? 0;
      const compensatedTime = currentTime - (latencyComp / 1000);
      const window = presetForFeedback.timingWindow ?? 0.05;

      const feedbackPoints = currentAnalysisRef.current.feedbackPoints.filter(
        (point) => Math.abs(point.timestamp - compensatedTime) < window
      );

      if (feedbackPoints.length > 0) {
        const totalIntensity = Math.min(
          feedbackPoints.reduce((sum, point) => sum + point.intensity * presetForFeedback.intensity, 0),
          1 
        );

        if ("vibrate" in navigator) {
          navigator.vibrate(Math.floor(totalIntensity * presetForFeedback.duration)); 
        }
      }
    },
    [settings.enabled, settings.currentPreset, livePresetConfig] // Added livePresetConfig
  );

  const toggleEnabled = useCallback(() => {
    setSettings((prev) => {
      const newSettings = { ...prev, enabled: !prev.enabled };
      console.log("=== hapticsengine: Haptics feedback:", newSettings.enabled ? "enabled" : "disabled");

      // Provide feedback when enabling
      if (newSettings.enabled && "vibrate" in navigator) {
        // Triple vibration pattern to indicate enabled state
        navigator.vibrate([100, 100, 100, 100, 100]);
      }

      return newSettings;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
        audioContextRef.current = null;
        analyzerRef.current = null;
      }
    };
  }, []);

  return {
    analyzeTrack,
    provideFeedback,
    settings,
    toggleEnabled,
    updateSettings,
    isSupported: "vibrate" in navigator,
    livePresetConfig,
    updateLivePresetConfig,
  };
};
